import React, { Fragment, useEffect, useState } from "react";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import { useNavigate, useLocation } from "react-router";
import { ArrowBack } from "@mui/icons-material";
import Typography from "@mui/material/Typography";
import InputField2 from "../../common/InputComponents/InputField2";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormProvider, useForm } from "react-hook-form";
import ToggleButtons from "../../common/InputComponents/ToggleButtons";
import ClearIcon from "@mui/icons-material/Clear";
import CheckIcon from "@mui/icons-material/Check";
import { Box, Button } from "@mui/material";
import {
  Loading,
  useCreate,
  useGetIdentity,
  useGetList,
  useGetOne,
  useNotify,
  useRefresh,
  useStore,
} from "react-admin";
import InputSelectField from "../../common/InputComponents/InputSelectField";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import InputCurrencyField from "../../DocumentAnalysis/components/inputComponents/InputCurrencyField";
import save_icon from "../../../assets/images/save_icon.svg";
import CopyUrls from "./CopyUrls";
import formValidation from "../../../yupFormValidationRules/formValidator";
import { REACT_APPLICANT } from "../../../config";
import { resolveSelectedSubscriberId } from "./resolveSelectedSubscriberId";

const AddAndUpdateFloor = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const notify = useNotify();
  const refresh = useRefresh();
  const toggleButton = [
    {
      value: 0,
      label: "Inactive",
      icon: <ClearIcon />,
      borderColor: "#909090",
      backgroundColor: "#EEE",
      boxShadowColor: "#90909033",
      color: "#606060",
    },
    {
      value: 1,
      label: "Active",
      icon: <CheckIcon />,
      borderColor: "#46A96F",
      backgroundColor: "#EBFFF3",
      boxShadowColor: "#46a96f1a",
      color: "#46A96F",
    },
  ];
  const [switchSubscriberObj] = useStore("switchSubscriberName");
  const {  identity } = useGetIdentity();
  const { id, floorId } = useParams();
  const selectedSubscriberId = resolveSelectedSubscriberId(
    identity,
    switchSubscriberObj
  );
  // get application detail data
  const { data: applicationDetail, isLoading: isLoadingData } = useGetOne(
    "getnewapplicationdetail",
    {
      id: selectedSubscriberId,
    },
    {
      enabled: selectedSubscriberId ? true : false,
      refetchOnWindowFocus: false,
    }
  );

  // get floor details
  const { data: getFloorDetails, isLoading: loadingFloorDetails } = useGetList(
    "get-floor",
    {
      filter: {
        propertyID: id,
        subscriberID: selectedSubscriberId,
        floorID: floorId,
      },
    },
    {
      enabled: selectedSubscriberId && id && floorId ? true : false,
      refetchOnWindowFocus: false,
    }
  );
// form validation schema
  const validationSchema = yup.lazy((value) =>
    yup.object().shape({
      floorCode: formValidation.name,
      property: formValidation.validationForDropDown,
      active: formValidation.StringRequiredValidation,
      minimumRent: yup
        .string()
        .matches(/^(\d*[0-9])|^$/, "Only Numbers Allowed")
        .min(1, "Greater than 0 value required")
        .nullable()
        .test("rent", "Required!", async (value) => {
          return value == null || parseInt(value) > 0 ? true : false;
        })
        .test(
          "min-less-than-max",
          "Minimum rent must be less than maximum rent.",
          (value: any, context) => {
            const { maximumRent } = context.parent;
            return +value <= +maximumRent;
          }
        ),
      maximumRent: yup
        .string()
        .matches(/^(\d*[0-9])|^$/, "Only Numbers Allowed")
        .min(1, "Greater than 0 value required")
        .nullable()
        .test("rent", "Required!", async (value) => {
          return value == null || parseInt(value) > 0 ? true : false;
        })
        .test(
          "max-greater-than-min",
          "Maximum rent must be greater than or equal to minimum rent.",
          (value: any, context) => {
            const { minimumRent } = context.parent;
            return +value >= +minimumRent;
          }
        ),
      deposit: yup
        .string()
        .matches(/^(\d*[0-9])|^$/, "Only Numbers Allowed")
        .min(1, "Greater than 0 value required")
        .nullable()
        .test("rent", "Required!", async (value) => {
          return value == null || parseInt(value) > 0 ? true : false;
        }),
    })
  );

  // Initial form values
  const defaultFormValues = {
    floorCode: "",
    property: "",
    active: 1,
    minimumRent: "",
    maximumRent: "",
    deposit: "",
  };

  const methods = useForm({
    defaultValues: defaultFormValues,
    resolver: yupResolver(validationSchema),
    mode: "onChange",
  });

  useEffect(() => {
    if (!state?.isAdding) {
      if (getFloorDetails && getFloorDetails?.length > 0) {
        getFloorDetails &&
          getFloorDetails?.map((floorDetails, index) => {
            applicationDetail?.Properties?.filter((item) => {
              if (item?.ID == floorDetails?.PropertyID) {
                let body = {
                  value: item?.ID,
                  label: item.PropertyName,
                };

                floorDetails.PropertyID = body;
              }
            });
          });
      }

      let data = getFloorDetails && getFloorDetails[0];
      const getFormData: any = {
        floorCode: data?.FloorCode,
        property: data?.PropertyID,
        active: data?.Active ? 1 : 0,
        minimumRent: data?.MinimumRent,
        maximumRent: data?.MaximumRent,
        deposit: data?.Deposit,
      };
      methods.reset(getFormData);
    }
  }, [getFloorDetails && getFloorDetails[0], methods, applicationDetail]);

  useEffect(() => {
    if (state?.isAdding) {
      let property: any = null;
      if (id) {
        applicationDetail?.Properties?.filter((item) => {
          if (item?.ID === id) {
            property = {
              value: item?.ID,
              label: item.PropertyName,
            };
          }
        });
      }
      methods.setValue("property", property);
    }
  }, [methods, id, applicationDetail]);

  const [create, { isLoading, error }] = useCreate();

  // form submit handler
  const onSubmit = (value) => {
    const addData = { ...value, subscriberID: selectedSubscriberId };
    const updateData = { ...value, ID: floorId };

    create(
      state?.isAdding ? `add-floor` : `update-floor`,
      { data: state?.isAdding ? addData : updateData },
      {
        onSuccess: (data) => {
          notify(
            state?.isAdding
              ? `Floor created successfully.`
              : `Floor updated successfully.`,
            {
              type: "success",
              anchorOrigin: { vertical: "top", horizontal: "right" },
            }
          );
          refresh();
          methods?.reset();
          navigate(`/ats/settings/propertySetup/propertyDetails/${state?.propertyId}`, { state: { type: "floor" } });
          // navigate(-1);
        },
        onError: (e: any) => {
          notify(`Operation fail:${e.message}`, {
            type: "error",
            anchorOrigin: { vertical: "top", horizontal: "right" },
          });
        },
      }
    );
  };

  useEffect(() => {
    document.title = state?.isAdding
      ? "Property Setup - Add New Floor"
      : "Property Setup - Floor Details";
  }, []);

  if (isLoadingData || loadingFloorDetails) return <Loading />

  return (
    <Fragment>
      <div className="home_content">
        <div className="row">
          <div className="col-12 d-flex">
            <Breadcrumbs aria-label="breadcrumb">
              <Typography sx={{ fontWeight: "bold" }}>
                <button
                  title="Go Back"
                  style={{ color: "#2cc0e4" }}
                  className="btn btn-link"
                  onClick={() => navigate(`/ats/settings/propertySetup/propertyDetails/${state?.propertyId}`, { state: { type: "floor" } })
                  }
                >
                  <ArrowBack />
                </button>
                {/* <span>Email Settings &gt; Template Details</span> */}
                <span>
                  {state?.isAdding ? `Add New Floor` : `Floor Details`}
                </span>
              </Typography>
            </Breadcrumbs>
          </div>
        </div>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} className="mr-t30">
            <div className="row">
              <div className="col-sm-12 col-md-4 col-lg-4 mr-b20">
                <InputField2
                  label="Floor Code"
                  source={`floorCode`}
                  placeholder="Enter Floor Code"
                  labelClass={"lable-title"}
                  additionalInputClass={"cus-control"}
                />
              </div>
              <div className="col-sm-12 col-md-4 col-lg-4 mr-b20">
                <InputSelectField
                  label={`Property`}
                  source={`property`}
                  options={applicationDetail?.Properties?.map((item) => ({
                    value: item?.ID,
                    label: item.PropertyName,
                  }))}
                  labelAsteriskClass={"color-red"}
                  labelClass={"lable-title"}
                  disable={true}
                  requiredClass={{
                    fontSize: "70%",
                    zIndexValue: 2
                  }}
                />
              </div>
              <div className="col-sm-12 col-md-4 col-lg-4 mr-b20">
                <ToggleButtons
                  toggleButton={toggleButton}
                  label="Status"
                  labelClass={"lable-title"}
                  source={`active`}
                />
              </div>
              <div className="col-sm-12 col-md-4 col-lg-4 mr-b20">
                <InputCurrencyField
                  label="Minimum Rent"
                  source={`minimumRent`}
                  required={true}
                  labelClass={"lable-title"}
                  requiredClass={{
                    fontSize: "70%",
                  }}
                  handleOnChange={() => {
                    let watchMaximumRent: any = methods?.watch(`maximumRent`);
                    if (
                      watchMaximumRent &&
                      watchMaximumRent !== "" &&
                      watchMaximumRent !== 0
                    ) {
                      methods?.trigger(`minimumRent`);
                      methods?.trigger(`maximumRent`);
                    }
                  }}
                />
              </div>
              <div className="col-sm-12 col-md-4 col-lg-4 mr-b20">
                <InputCurrencyField
                  label="Maximum Rent"
                  source={`maximumRent`}
                  required={true}
                  labelClass={"lable-title"}
                  requiredClass={{
                    fontSize: "70%",
                  }}
                  handleOnChange={() => {
                    let watchMinimumRent: any = methods?.watch(`minimumRent`);
                    if (
                      watchMinimumRent &&
                      watchMinimumRent !== "" &&
                      watchMinimumRent !== 0
                    ) {
                      methods?.trigger(`minimumRent`);
                      methods?.trigger(`maximumRent`);
                    }
                  }}
                />
              </div>
              <div className="col-sm-12 col-md-4 col-lg-4 mr-b20">
                <InputCurrencyField
                  label="Deposit"
                  source={`deposit`}
                  required={true}
                  labelClass={"lable-title"}
                  requiredClass={{
                    fontSize: "70%",
                  }}
                  handleOnChange={() => {
                    methods?.trigger(`deposit`);
                  }}
                />
              </div>
            </div>

            {!state?.isAdding && (
              <div className="row mr-b50">
                {getFloorDetails &&
                  getFloorDetails[0]?.SubscriberAts &&
                  getFloorDetails &&
                  getFloorDetails[0]?.URL ? (
                  <div className="col-12 mr-b10">
                    <CopyUrls
                      heading={"Floor URL - Application"}
                      url={getFloorDetails && (REACT_APPLICANT + '/' + getFloorDetails[0]?.URL)}
                    />
                  </div>
                ) : (
                  <></>
                )}
                {getFloorDetails &&
                  getFloorDetails[0]?.SubscriberDocUpload &&
                  getFloorDetails &&
                  getFloorDetails[0]?.DocUploadURL ? (
                  <div className="col-12 mr-b10">
                    <CopyUrls
                      heading={"Floor URL - Doc Upload"}
                      url={getFloorDetails && (`${window.location.protocol}//${window.location.host}/atss/cp/` + getFloorDetails[0]?.DocUploadURL)}
                    />
                  </div>
                ) : (
                  <></>
                )}
                {getFloorDetails &&
                  getFloorDetails[0]?.SubscriberGuestCard &&
                  getFloorDetails &&
                  getFloorDetails[0]?.GuestCardURL ? (
                  <div className="col-12 mr-b10">
                    <CopyUrls
                      heading={"Floor URL - Guest Card"}
                      url={getFloorDetails && (`${window.location.protocol}//${window.location.host}/atss/cp/` + getFloorDetails[0]?.GuestCardURL)}
                    />
                  </div>
                ) : (
                  <></>
                )}
              </div>
            )}

            <Box
              display={"flex"}
              justifyContent={"end"}
              alignItems={"center"}
              className="mt-4"
              minHeight="10vh"
              position={"fixed"}
              bottom={70}
              right={30}
            >
              <Box>
                <Button
                  sx={{ mx: 1 }}
                  onClick={() => {
                    refresh();
                    methods?.reset();
                    navigate(`/ats/settings/propertySetup/propertyDetails/${state?.propertyId}`, { state: { type: "floor" } });
                  }}
                  variant="outlined"
                  color="secondary"
                  type="button"
                >
                  Cancel
                </Button>
                <Button
                  startIcon={
                    state?.isAdding ? (
                      <CheckIcon />
                    ) : (
                      <img src={save_icon} alt="save icon" />
                    )
                  }
                  sx={{ mx: 1 }}
                  variant="contained"
                  type="submit"
                  disabled={isLoading}
                >
                  {state?.isAdding ? `Create Floor` : `Update`}
                </Button>
              </Box>
            </Box>
          </form>
        </FormProvider>
      </div>
    </Fragment>
  );
};

export default AddAndUpdateFloor;
